import L from "leaflet";
import type { CatalogLayer } from "./catalog";

export interface LayerCreateOptions {
  pane?: string;
}

// Optional sink for the first per-layer decode error, so the UI can surface
// it to the user (set by main.ts). Kept module-level to avoid threading a
// callback through every factory signature.
let decodeErrorSink: ((layerId: string, title: string, err: unknown) => void) | null = null;
export function setDecodeErrorSink(fn: typeof decodeErrorSink): void {
  decodeErrorSink = fn;
}

// Web Mercator (EPSG:3857) world extent in meters, and the tile pixel size.
const WEBMERC_ORIGIN = 20037508.342789244;
const TILE_SIZE = 256;

// Optional caching reverse-proxy for EXTERNAL tile sources. When TILE_PROXY is a
// non-empty host, every external (non-self-hosted) tile / dynamic-export / WMS
// request routes through our CloudFront caching proxy instead of hitting the
// upstream directly. This (a) BOUNDS upstream load — many viewers collapse to ~one
// origin fetch per unique tile globally instead of N×tiles — protecting small
// county/city GIS servers as traffic grows; (b) lets the proxy inject CORS so a
// source revoking CORS can't break us; and (c) presents a neutral Origin upstream
// so a source can't hotlink-block our domain. Our own COGs (cogs.…) are never
// proxied — they're already on our CDN. Format: `<proxy>/<url-without-scheme>` so
// Leaflet's {z}/{x}/{y} placeholders and query strings pass through untouched.
// Ship inert (""); flip to the proxy host after the infra (Terraform) is applied.
const TILE_PROXY: string = "https://tileproxy.mapbeast.net";

// Hosts that must stay browser-direct — the CDN-scale providers. Proxying a
// high-volume CDN through CloudFront gives no benefit (they're built for internet
// scale), adds our egress cost, and CONCENTRATES our traffic onto a few CloudFront
// egress IPs — which trips their bot/rate-limit detection. OSM proved it: a proxied
// request gets a 6.9 KB `x-blocked: Access denied` placeholder instead of the tile
// (direct 38 KB, verified 2026-07-10); Esri/USGS could do the same. The proxy's value
// is bounding load on SMALL fragile gov servers, so only those are proxied. Keep this
// in sync with NO_PROXY (browser-direct) in scripts/tileproxy-allowlist.py, which
// derives the Lambda ALLOW set as (catalog external hosts − these).
const NO_PROXY_HOSTS = [
  "tile.openstreetmap.org",
  "tiles.arcgis.com",
  "server.arcgisonline.com",
  "services.arcgisonline.com",
  "basemap.nationalmap.gov",
];

function proxied(url: string): string {
  if (!TILE_PROXY) return url;
  if (!/^https?:\/\//i.test(url)) return url; // data:/blob:/relative — leave alone
  if (url.includes("cogs.mapbeast.net")) return url; // our own CDN
  if (NO_PROXY_HOSTS.some((h) => url.includes(h))) return url;
  return `${TILE_PROXY}/${url.replace(/^https?:\/\//i, "")}`;
}

// XYZ tile coords → EPSG:3857 bbox [minX, minY, maxX, maxY]. Shared by the
// ArcGIS-dynamic /export factory and the COG tile factory — both translate a
// Leaflet tile coord into a Web Mercator extent the exact same way.
function tileBbox3857(coords: L.Coords): [number, number, number, number] {
  const n = Math.pow(2, coords.z);
  const tileExtent = (2 * WEBMERC_ORIGIN) / n;
  const minX = -WEBMERC_ORIGIN + coords.x * tileExtent;
  const maxX = minX + tileExtent;
  const maxY = WEBMERC_ORIGIN - coords.y * tileExtent;
  const minY = maxY - tileExtent;
  return [minX, minY, maxX, maxY];
}

export async function createLayer(
  layer: CatalogLayer,
  opts: LayerCreateOptions = {}
): Promise<L.Layer> {
  // maxNativeZoom: stop requesting from source past its zoom limit (Leaflet
  // will upscale the highest available tiles). maxZoom: how far the user can
  // zoom while this layer is on.
  // crossOrigin: "anonymous" forces CORS-mode fetch — required for some
  // ArcGIS-hosted layers that set nosniff but no CORP header, which trips
  // Chrome's Opaque Response Blocking on the default no-cors image fetch.
  const tileOpts: L.TileLayerOptions = {
    attribution: layer.attribution,
    opacity: layer.default_opacity ?? 1.0,
    maxZoom: 22,
    maxNativeZoom: layer.max_zoom ?? 22,
    minZoom: layer.min_zoom ?? 0,
    // minNativeZoom: the lowest zoom the tile CACHE actually has real tiles at.
    // Many ArcGIS tile caches only build z13-15+ (lower LODs are blank 872-byte
    // placeholders), so requesting native tiles below that renders nothing. Setting
    // this makes Leaflet fetch the floor-zoom tiles and DOWNSCALE them for lower
    // display zooms (down to minZoom) instead of showing blank. Falls back to
    // min_zoom for layers whose cache floors at 0.
    minNativeZoom: layer.min_native_zoom ?? layer.min_zoom ?? 0,
    pane: opts.pane,
    crossOrigin: "anonymous",
  };
  if (layer.subdomains) tileOpts.subdomains = layer.subdomains;
  // Non-256px tile sources (e.g. MapTiler 512px raster tiles): set the tile
  // size and the matching zoomOffset so Leaflet's z/x/y line up — a 512px
  // tile grid is one zoom level "coarser" than the 256px default.
  if (layer.tile_size && layer.tile_size !== 256) {
    tileOpts.tileSize = layer.tile_size;
    tileOpts.zoomOffset = Math.round(Math.log2(256 / layer.tile_size));
  }

  switch (layer.type) {
    case "xyz":
    case "mapwarper":
      return L.tileLayer(proxied(layer.url), tileOpts);

    case "wms":
      if (!layer.wms_layer) throw new Error(`${layer.id}: wms layer missing wms_layer`);
      return L.tileLayer.wms(proxied(layer.url), {
        ...tileOpts,
        layers: layer.wms_layer,
        format: layer.wms_format ?? "image/png",
        transparent: layer.wms_transparent ?? true,
      });

    case "wmts":
      return createWMTSLayer(layer, tileOpts);

    case "cog":
      return await createCogLayer(layer, opts);

    case "arcgis-dynamic":
      return createArcGISDynamicLayer(layer, opts);

    default: {
      const _exhaustive: never = layer.type;
      throw new Error(`Unknown layer type: ${_exhaustive}`);
    }
  }
}

/**
 * WMTS (OGC Web Map Tile Service) — used for David Rumsey georeferenced maps
 * served via maps.georeferencer.com. Two forms:
 *
 *  - RESTful: if `url` already contains a `{z}` placeholder it's a ready
 *    tile template — used directly, exactly like an `xyz` layer.
 *  - KVP: otherwise build a WMTS GetTile request from wmts_layer / wmts_style
 *    / wmts_tilematrixset / wmts_format. WMTS GetTile takes TILEMATRIX (zoom),
 *    TILEROW (y) and TILECOL (x); we leave those as Leaflet {z}/{y}/{x}
 *    placeholders. This assumes the layer's tile matrix set is the standard
 *    Web-Mercator quad (GoogleMapsCompatible) so the matrix identifiers are
 *    plain integers — true for georeferencer/Rumsey. A service with a
 *    non-integer TILEMATRIX scheme should be cataloged with a RESTful url.
 */
function createWMTSLayer(layer: CatalogLayer, tileOpts: L.TileLayerOptions): L.Layer {
  // RESTful / already-templated URL — nothing to construct.
  if (layer.url.includes("{z}")) return L.tileLayer(proxied(layer.url), tileOpts);

  if (!layer.wmts_layer) {
    throw new Error(`${layer.id}: wmts layer needs wmts_layer (or a {z}/{x}/{y} url)`);
  }
  // Static GetTile params. URLSearchParams percent-encodes values safely;
  // the {z}/{y}/{x} placeholders are appended raw afterward so Leaflet can
  // substitute them (URLSearchParams would escape the braces).
  const params = new URLSearchParams({
    SERVICE: "WMTS",
    REQUEST: "GetTile",
    VERSION: "1.0.0",
    LAYER: layer.wmts_layer,
    STYLE: layer.wmts_style ?? "default",
    TILEMATRIXSET: layer.wmts_tilematrixset ?? "GoogleMapsCompatible",
    FORMAT: layer.wmts_format ?? "image/png",
  });
  const sep = layer.url.includes("?") ? "&" : "?"; // url may carry ?key=…
  const url = `${layer.url}${sep}${params.toString()}` +
    "&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}";
  return L.tileLayer(proxied(url), tileOpts);
}

/**
 * Wraps an ArcGIS REST MapServer/ImageServer that has no tile cache (i.e.
 * singleFusedMapCache=false) by translating XYZ tile coords to a Web Mercator
 * bbox and hitting the /export endpoint per tile. Slower than a tile cache —
 * each tile is rendered on demand — but works against services that only
 * expose dynamic /export.
 *
 * Used by the 15 arcgis-dynamic layers in the catalog. (Note: USGS
 * topoView's ustOverlayAuto looks tempting here — it renders fine — but
 * it serves coverage-polygon overlays only, not actual topo map content.)
 */
function createArcGISDynamicLayer(layer: CatalogLayer, opts: LayerCreateOptions): L.Layer {
  const url = proxied(layer.url); // base URL ending in /MapServer (no trailing slash)
  // Per-service extras (e.g. {"layers": "show:6"} for multi-layer MapServers).
  // Merged into the URL query but bbox/SR/format/size always win.
  const extraParams = layer.arcgis_export_params ?? {};

  const ArcGISDynamicLayer = L.TileLayer.extend({
    getTileUrl(coords: L.Coords) {
      const [minX, minY, maxX, maxY] = tileBbox3857(coords);
      // Most services accept the tile bbox in 3857 directly. Some (e.g. CAGIS,
      // CT ECO — State-Plane-cached MapServers) only return imagery when the
      // bbox is expressed in lon/lat (bboxSR=4326); opt in per-layer via
      // `arcgis_bbox_sr: "4326"`. Output stays 3857 (imageSR) so the tile aligns.
      const bboxSR = layer.arcgis_bbox_sr ?? "3857";
      let bboxStr = `${minX},${minY},${maxX},${maxY}`;
      if (bboxSR === "4326") {
        const R = 20037508.342789244;
        const m2ll = (x: number, y: number): [number, number] => [
          (x / R) * 180,
          (Math.atan(Math.exp((y / R) * Math.PI)) * 360) / Math.PI - 90,
        ];
        const [w, s] = m2ll(minX, minY);
        const [e, n] = m2ll(maxX, maxY);
        bboxStr = `${w},${s},${e},${n}`;
      }
      const params = new URLSearchParams({
        ...extraParams,
        bbox: bboxStr,
        bboxSR,
        imageSR: "3857",
        size: `${TILE_SIZE},${TILE_SIZE}`,
        format: "png",
        transparent: "true",
        f: "image",
      });
      const exportPath = url.endsWith("/ImageServer") ? "/exportImage" : "/export";
      return `${url}${exportPath}?${params.toString()}`;
    },
  });

  return new (ArcGISDynamicLayer as any)(null, {
    attribution: layer.attribution,
    opacity: layer.default_opacity ?? 1.0,
    maxZoom: 22,
    maxNativeZoom: layer.max_zoom ?? 22, // same default as the tile-layer path
    minZoom: layer.min_zoom ?? 0,
    // Same fallback chain as the tile-layer path — honor an explicit
    // min_native_zoom (fetch floor-zoom exports and downscale below it).
    minNativeZoom: layer.min_native_zoom ?? layer.min_zoom ?? 0,
    pane: opts.pane,
    crossOrigin: "anonymous",
  }) as L.Layer;
}

// Cache parsed TIFFs across activate/deactivate cycles. fromUrl() does its
// own HTTP byte-range cache internally, but constructing the GeoTIFF object
// + reading the IFD still costs a round trip to the origin. Keeping the
// instance avoids that whenever the user toggles a layer they've used before.
// LRU-capped: each instance holds a BlockedSource full of fetched byte ranges,
// so an unbounded cache grows with every COG ever toggled in the session.
// Map iteration order = insertion order; re-inserting on hit makes it LRU.
const tiffCache = new Map<string, Promise<any>>();
const TIFF_CACHE_LIMIT = 10;

// One decoder pool shared across all COG layers. The pool spawns Web Workers
// that handle JPEG (and other) decoding off the main thread — without it,
// each readRGB call's JPEG decode runs synchronously on the main thread,
// causing visible UI freezes during pan/zoom whenever a tile is decoding.
let decoderPool: any | null = null;
function ensurePool(PoolCtor: any): any {
  if (!decoderPool) decoderPool = new PoolCtor();
  return decoderPool;
}

// Per-tile result cache, shared across layers. Keyed by `${layerId}|${z}|${x}|${y}`.
// Without this, every revisit to a previously-rendered tile (zoom out + back in,
// pan beyond keepBuffer + back) re-triggers the full readRGB → JPEG decode →
// RGB-to-RGBA copy pipeline. With this, the second visit just blits the cached
// pixel data — synchronous, near-instant.
//
// Memory accounting: each cached entry holds a Uint8ClampedArray of dstW × dstH
// × 4 bytes. Worst case (tile fully covered by the COG) = 256 × 256 × 4 = 256 KB.
// At TILE_CACHE_LIMIT = 400 that's ~100 MB max. The actual hit rate matters more
// than the cap; LRU eviction (insertion-order) keeps the most recently produced.
type CachedTile = {
  data: Uint8ClampedArray;
  dstW: number;
  dstH: number;
  dstX0: number;
  dstY0: number;
};
const tilePixelCache = new Map<string, CachedTile>();
const tilePixelCacheOrder: string[] = [];
// Halve the budget (~50 MB) on low-memory devices. navigator.deviceMemory is
// Chrome-only; browsers without it get the full desktop-sized cap.
const TILE_CACHE_LIMIT = ((navigator as any).deviceMemory ?? 8) <= 4 ? 200 : 400;
function cacheTile(key: string, entry: CachedTile): void {
  if (tilePixelCache.has(key)) return; // first-write wins; same key always yields same pixels
  tilePixelCache.set(key, entry);
  tilePixelCacheOrder.push(key);
  while (tilePixelCacheOrder.length > TILE_CACHE_LIMIT) {
    const oldKey = tilePixelCacheOrder.shift();
    if (oldKey !== undefined) tilePixelCache.delete(oldKey);
  }
}

/**
 * COG layer driven by geotiff.js directly, bypassing georaster.
 *
 * Why not georaster: it bundles an ancient geotiff.js (1.0.0-beta.13, 2020)
 * whose JPEG decoder always returns YCbCr bytes labeled as RGB, which
 * renders as saturated pink for any JPEG-compressed COG. The fix is in
 * modern geotiff.js (3.x) as image.readRGB(), which auto-converts photometric.
 *
 * Tile coords → EPSG:3857 bbox → readRGB → canvas. Range requests come
 * from geotiff.js's BlockedSource, which is what makes COGs efficient.
 */
async function createCogLayer(layer: CatalogLayer, opts: LayerCreateOptions = {}): Promise<L.Layer> {
  const geotiff = await import("geotiff");
  const pool = ensurePool(geotiff.Pool);

  // Cache hit: the IFD is already parsed and the BlockedSource has its
  // header bytes warm. Cache miss: parse it once and remember.
  // If a cached promise rejects, evict it so a later activate can retry
  // instead of inheriting a permanently-failed parse.
  let tiffPromise = tiffCache.get(layer.url);
  if (tiffPromise) {
    // LRU refresh: re-insert so this entry moves to the back of eviction order.
    tiffCache.delete(layer.url);
    tiffCache.set(layer.url, tiffPromise);
  } else {
    tiffPromise = geotiff.fromUrl(layer.url);
    tiffPromise.catch(() => tiffCache.delete(layer.url));
    tiffCache.set(layer.url, tiffPromise);
    while (tiffCache.size > TIFF_CACHE_LIMIT) {
      const oldest = tiffCache.keys().next().value;
      if (oldest === undefined) break;
      tiffCache.delete(oldest);
    }
  }
  const tiff = await tiffPromise;

  // Load all IFDs: IFD 0 is full resolution, higher indices are overview
  // pyramids (typically 1/2, 1/4, 1/8, ...). For big COGs at low zoom we
  // MUST read from overviews — pulling tile windows from the full-res IFD
  // means hundreds of parallel range requests for each visible tile, which
  // exhausts Chrome's socket pool (ERR_INSUFFICIENT_RESOURCES). For tiny
  // COGs this is a no-op since there's typically only one IFD.
  //
  // Parallelise the per-IFD metadata fetch — each getImage() may trigger a
  // range request for that IFD's tags, and an 8-IFD COG (full + 7 overviews)
  // was previously costing 8 sequential round trips before the first tile
  // could be requested. Promise.all collapses that into one RTT in parallel.
  const imageCount = await tiff.getImageCount();
  const images = await Promise.all(
    Array.from({ length: imageCount }, (_, i) => tiff.getImage(i))
  );

  // Base IFD defines the geo origin and total extent. Overview IFDs (>0)
  // typically OMIT geotransform tags — they're just downsampled pixel
  // arrays. Calling getOrigin() or getResolution() on an overview throws
  // "image does not have an affine transformation". Compute everything
  // from IFD 0 and derive per-overview resolution from the width ratio.
  const baseImage = images[0];
  const [origX, origY] = baseImage.getOrigin();
  const [baseResX, baseResY] = baseImage.getResolution();
  const baseW = baseImage.getWidth();
  const baseH = baseImage.getHeight();
  const srcBbox = [origX, origY + baseResY * baseH, origX + baseResX * baseW, origY];

  // Per-IFD resolution = base_res × (base_width / ifd_width). Use abs() so
  // the overview-picker comparison is in positive units regardless of sign.
  const ifdResolutions = images.map((img) => {
    const ratio = baseW / img.getWidth();
    return Math.abs(baseResX) * ratio;
  });

  // Set when the layer is removed from the map. In-flight readRGB() promises
  // resolve after removal; without this flag their .then would draw onto an
  // orphaned canvas / fire done() for a tile that no longer exists. Reset in
  // onAdd so a re-added layer instance works again.
  let aborted = false;
  // AbortControllers for in-flight readRGB() calls, so removal/unload can
  // cancel the underlying range fetches + decode instead of just ignoring
  // their results.
  const liveReads = new Set<AbortController>();
  // Surface only the first decode failure for this layer (mask-band COGs,
  // unsupported photometric, etc.) — don't spam the user per failed tile.
  let decodeErrorReported = false;

  // Leaflet evicts tiles as the user pans/zooms (anything beyond keepBuffer's
  // ring). If a tile's readRGB is still mid-flight when that happens, the
  // .then would otherwise pointlessly run the RGB→RGBA copy (256x256 = 65k
  // pixels × 4 writes) + putImageData + drawImage on a canvas that's no longer
  // in the DOM. Marking the tile cancelled lets the .then return early, and
  // aborting its controller cancels the fetch/decode itself — frees the JPEG
  // worker and main-thread time for the tile the user actually wants.
  // Named (not inline) so repeated onAdd cycles can .off before .on instead of
  // stacking a duplicate handler per re-add.
  const onTileUnload = (e: any) => {
    (e.tile as any).__cancelled = true;
    (e.tile as any).__abortCtl?.abort();
  };

  const CogTileLayer = L.GridLayer.extend({
    onAdd(map: L.Map) {
      aborted = false; // instance may be re-added after a remove
      this.off("tileunload", onTileUnload).on("tileunload", onTileUnload);
      return (L.GridLayer.prototype as any).onAdd.call(this, map);
    },
    onRemove(map: L.Map) {
      aborted = true;
      // Cancel every in-flight read — no point finishing fetches/decodes for
      // a layer that's gone.
      for (const ctl of [...liveReads]) ctl.abort();
      liveReads.clear();
      (L.GridLayer.prototype as any).onRemove.call(this, map);
    },
    createTile(coords: L.Coords, done: (err: any, tile: HTMLCanvasElement) => void) {
      const tile = document.createElement("canvas");
      tile.width = TILE_SIZE;
      tile.height = TILE_SIZE;

      // FAST PATH: if this tile was rendered recently (and is still in our LRU
      // cache), blit the cached pixels and return immediately. Avoids the entire
      // async readRGB → JPEG decode → RGB-to-RGBA copy pipeline on a revisit.
      // The cache key is fully determined by the COG (layer.id) + tile coords,
      // so the same key always yields the same pixels.
      const cacheKey = `${layer.id}|${coords.z}|${coords.x}|${coords.y}`;
      const cached = tilePixelCache.get(cacheKey);
      if (cached) {
        const ctx = tile.getContext("2d");
        if (ctx) {
          const tmp = document.createElement("canvas");
          tmp.width = cached.dstW;
          tmp.height = cached.dstH;
          const tctx = tmp.getContext("2d");
          if (tctx) {
            const img = tctx.createImageData(cached.dstW, cached.dstH);
            img.data.set(cached.data);
            tctx.putImageData(img, 0, 0);
            ctx.drawImage(tmp, Math.round(cached.dstX0), Math.round(cached.dstY0));
          }
        }
        // Defer done() one microtask so Leaflet finishes wiring the tile to the
        // DOM before being told it's ready — calling done() synchronously inside
        // createTile sometimes confuses GridLayer's loading state.
        setTimeout(() => done(null, tile), 0);
        return tile;
      }

      // Tile bbox in EPSG:3857
      const [tMinX, tMinY, tMaxX, tMaxY] = tileBbox3857(coords);
      const tileExtent = tMaxX - tMinX;

      // Quick reject: tile entirely outside layer
      if (tMaxX < srcBbox[0] || tMinX > srcBbox[2] || tMaxY < srcBbox[1] || tMinY > srcBbox[3]) {
        setTimeout(() => done(null, tile), 0);
        return tile;
      }

      // Tile resolution in source-CRS units per pixel (here EPSG:3857
      // meters/pixel, since the tile is TILE_SIZE wide spanning tileExtent
      // meters). Pick the smallest IFD whose resolution is still finer than
      // what this tile needs — that's the cheapest read that doesn't lose
      // detail. Fall back to IFD 0 (finest) if all overviews are coarser.
      const tileRes = tileExtent / TILE_SIZE;
      let chosenIdx = 0;
      for (let i = ifdResolutions.length - 1; i >= 0; i--) {
        if (ifdResolutions[i] <= tileRes) { chosenIdx = i; break; }
      }
      const image = images[chosenIdx];
      const sw = image.getWidth();
      const sh = image.getHeight();
      // Derive overview resolution from the width ratio (overviews don't
      // carry their own geotransform — see ifdResolutions comment above).
      const xScale = baseW / sw;
      const yScale = baseH / sh;
      const resX = baseResX * xScale;
      const resY = baseResY * yScale;

      // Map tile bbox → pixel window in the chosen IFD. Assumes source is
      // EPSG:3857 (the converter does the warp).
      const px = (x: number) => (x - origX) / resX;
      const py = (y: number) => (y - origY) / resY;
      const left = Math.max(0, Math.floor(px(tMinX)));
      const right = Math.min(sw, Math.ceil(px(tMaxX)));
      const top = Math.max(0, Math.floor(py(tMaxY)));
      const bottom = Math.min(sh, Math.ceil(py(tMinY)));

      if (right <= left || bottom <= top) {
        setTimeout(() => done(null, tile), 0);
        return tile;
      }

      // EPSG:3857 extent of the clamped pixel window — the sub-rectangle of
      // the tile the COG *actually* covers. We read the window at a size
      // matching its footprint inside the 256px tile and blit it there,
      // leaving the rest of the tile transparent. Reading the partial window
      // but stretching it to a full 256×256 tile was the "map grows when you
      // zoom out" bug: a small COG got smeared across the whole tile.
      const gx0 = origX + left * resX;
      const gx1 = origX + right * resX;
      const gyTop = origY + top * resY;
      const gyBot = origY + bottom * resY;
      const dstX0 = ((gx0 - tMinX) / tileExtent) * TILE_SIZE;
      const dstY0 = ((tMaxY - gyTop) / tileExtent) * TILE_SIZE;
      const dstW = Math.max(1, Math.round(((gx1 - gx0) / tileExtent) * TILE_SIZE));
      const dstH = Math.max(1, Math.round(((gyTop - gyBot) / tileExtent) * TILE_SIZE));

      // Cancellable read: tileunload / onRemove abort this controller, which
      // cancels the range fetches + decode inside geotiff.js (readRGB takes a
      // standard AbortSignal via its `signal` option).
      const abortCtl = new AbortController();
      (tile as any).__abortCtl = abortCtl;
      liveReads.add(abortCtl);
      image
        .readRGB({
          window: [left, top, right, bottom],
          width: dstW,
          height: dstH,
          interleave: true,
          enableAlpha: false,
          pool, // off-thread JPEG decode
          signal: abortCtl.signal,
        } as any)
        .then((data: any) => {
          liveReads.delete(abortCtl);
          // Layer was removed while this decode was in flight — drop it.
          if (aborted) return;
          // Tile was scrolled/zoomed out of view (keepBuffer ring) while we
          // were decoding — skip the (expensive) blit; done() is a no-op on
          // unloaded tiles. Saves real CPU on rapid pans.
          if ((tile as any).__cancelled) return done(null, tile);
          const ctx = tile.getContext("2d");
          if (!ctx) return done(null, tile);
          // RGB → RGBA into a temp canvas sized to the window's footprint,
          // then drawImage it into the tile at its true offset. The tile
          // canvas starts transparent, so uncovered area stays transparent.
          const tmp = document.createElement("canvas");
          tmp.width = dstW;
          tmp.height = dstH;
          const tctx = tmp.getContext("2d");
          if (!tctx) return done(null, tile);
          const img = tctx.createImageData(dstW, dstH);
          const out = img.data;
          // transparent_white: merged map/atlas/Sanborn COGs fill the gaps BETWEEN
          // scattered sheets (and the area outside a single warped sheet) with pure
          // white (compositing bg=255), which survives YCbCr-JPEG as flat 255 while
          // the sheets' own paper is cream (<=250). When the layer opts in, render
          // near-pure-white as transparent so the basemap shows through the gaps
          // instead of a sea of opaque white. Aerials/imagery don't set this (their
          // white is real data — snow, rooftops, clouds).
          const wT = (layer as any).transparent_white === true;
          for (let i = 0, j = 0; i < data.length; i += 3, j += 4) {
            const r = data[i], g = data[i + 1], b = data[i + 2];
            out[j] = r;
            out[j + 1] = g;
            out[j + 2] = b;
            out[j + 3] = (wT && r >= 250 && g >= 250 && b >= 250) ? 0 : 255;
          }
          tctx.putImageData(img, 0, 0);
          ctx.drawImage(tmp, Math.round(dstX0), Math.round(dstY0));
          // Stash the result so a revisit (zoom out + back, pan beyond
          // keepBuffer + back) blits synchronously instead of redoing the
          // whole async decode pipeline. Copy `out` since the ImageData
          // backing array is otherwise transferred-into the canvas.
          cacheTile(cacheKey, {
            data: new Uint8ClampedArray(out),
            dstW,
            dstH,
            dstX0,
            dstY0,
          });
          done(null, tile);
        })
        .catch((err: any) => {
          liveReads.delete(abortCtl);
          if (aborted) return;
          // A cancelled read (tileunload aborted the controller) rejects with
          // AbortError — expected, not a decode failure; stay quiet.
          if (abortCtl.signal.aborted || err?.name === "AbortError") {
            return done(null, tile);
          }
          // Surface the first decode failure to the user (e.g. a mask-band
          // COG that breaks readRGB); stay quiet for subsequent tiles.
          if (!decodeErrorReported) {
            decodeErrorReported = true;
            decodeErrorSink?.(layer.id, layer.title, err);
          }
          done(err, tile);
        });

      return tile;
    },
  });

  return new (CogTileLayer as any)({
    tileSize: TILE_SIZE,
    opacity: layer.default_opacity ?? 1.0,
    maxNativeZoom: layer.max_zoom ?? 22,
    minNativeZoom: layer.min_zoom ?? 0,
    minZoom: layer.min_zoom ?? 0,
    maxZoom: 22,
    pane: opts.pane,
    attribution: layer.attribution,
    // Defer tile fetches until the user stops moving / zooming. Leaflet's
    // default `updateWhenIdle: false` fires a fresh wave of createTile()
    // calls on every pan-pixel; the Pool's JPEG workers (and the main-thread
    // blit step) then queue up behind decodes for tiles the user has already
    // scrolled past. Idle-only updates feel dramatically smoother on COGs.
    updateWhenIdle: true,
    updateWhenZooming: false,
    // Keep 8 rings of off-screen tiles around the viewport (default 2). Small
    // pans then often hit Leaflet's tile-DOM cache and don't even reach our
    // createTile. Memory cost is modest because each tile is a 256x256 canvas
    // (~256 KB); 8 rings × ~8 tiles per viewport side ≈ 64 extra canvases =
    // ~16 MB per active layer, fine on desktop. Pairs with the pixel-cache
    // above for re-visits beyond this ring (or to a different zoom).
    keepBuffer: 8,
  });
}
